import { useState, useRef, useEffect } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMoon, faSun, faEyeSlash, faEye } from '@fortawesome/free-solid-svg-icons';
import styles from './ColorPalette.module.css';

export default function ColorPalette() {
  const { setCustomColor, customColor } = useTheme();
  const { t } = useLanguage();
  
  const [selectedColor, setSelectedColor] = useState(() => {
    try {
      return localStorage.getItem('customThemeColor') || customColor || '#1976d2';
    } catch {
      return customColor || '#1976d2';
    }
  });
  
  const [showPicker, setShowPicker] = useState(false);
  
  const [isDarkMode, setIsDarkMode] = useState(() => {
    try {
      const saved = localStorage.getItem('isDarkMode');
      return saved === 'true';
    } catch {
      return false;
    }
  });
  
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const presetColors = [
    '#1976d2', '#9c27b0', '#4caf50', '#ff9800', '#90caf9',
    '#e91e63', '#00bcd4', '#ffeb3b', '#ff5722', '#673ab7',
    '#009688', '#8bc34a', '#ffc107', '#795548', '#607d8b',
    '#f44336', '#3f51b5', '#cddc39', '#ff6f00', '#9e9e9e',
  ];

  useEffect(() => {
    try {
      const savedColor = localStorage.getItem('customThemeColor');
      const savedDarkMode = localStorage.getItem('isDarkMode') === 'true';
      
      if (savedColor) {
        setSelectedColor(savedColor);
        setIsDarkMode(savedDarkMode);
        generateThemeFromColor(savedColor, savedDarkMode);
      }
    } catch (error) {
      console.error('Error loading saved theme:', error);
    }
  }, []);

  useEffect(() => {
    if (!canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const width = canvas.width;
    const height = canvas.height;

    ctx.clearRect(0, 0, width, height);

    for (let x = 0; x < width; x++) {
      const hue = (x / width) * 360;
      
      for (let y = 0; y < height; y++) {
        const lightness = 90 - (y / height) * 60; 
        const saturation = 80 + (y / height) * 20; 
        
        ctx.fillStyle = `hsl(${hue}, ${saturation}%, ${lightness}%)`;
        ctx.fillRect(x, y, 1, 1);
      }
    }
  }, [showPicker]);

  const handleCanvasClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    const x = (e.clientX - rect.left) * scaleX;
    const y = (e.clientY - rect.top) * scaleY;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const imageData = ctx.getImageData(x, y, 1, 1);
    const [r, g, b] = imageData.data;
    
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    
    if (brightness < 50 || brightness > 240) {
      return;
    }
    
    const hexColor = `#${[r, g, b].map(c => c.toString(16).padStart(2, '0')).join('')}`;
    setSelectedColor(hexColor);
    generateThemeFromColor(hexColor, isDarkMode);
  };

  const handlePresetClick = (color: string) => {
    setSelectedColor(color);
    generateThemeFromColor(color, isDarkMode);
  };

  const generateThemeFromColor = (color: string, darkMode: boolean = isDarkMode) => {
    const hex = color.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);

    const darkerColor = `rgb(${Math.max(30, r - 30)}, ${Math.max(30, g - 30)}, ${Math.max(30, b - 30)})`;
    const lighterColor = `rgba(${r}, ${g}, ${b}, 0.1)`;

    const homeGradient = `linear-gradient(-45deg, 
      rgb(${Math.max(20, r - 40)}, ${Math.max(20, g - 40)}, ${Math.max(20, b - 40)}) 0%, 
      rgb(${Math.max(30, r - 20)}, ${Math.max(30, g - 20)}, ${Math.max(30, b - 20)}) 20%, 
      rgb(${r}, ${g}, ${b}) 40%, 
      rgb(${Math.min(255, r + 20)}, ${Math.min(255, g + 20)}, ${Math.min(255, b + 20)}) 60%, 
      rgb(${Math.min(255, r + 40)}, ${Math.min(255, g + 40)}, ${Math.min(255, b + 40)}) 80%, 
      rgb(${Math.min(255, r + 60)}, ${Math.min(255, g + 60)}, ${Math.min(255, b + 60)}) 100%)`;

    document.documentElement.style.setProperty('--primary', color);
    document.documentElement.style.setProperty('--primaryHover', darkerColor);
    document.documentElement.style.setProperty('--primaryLight', lighterColor);
    document.documentElement.style.setProperty('--background', darkMode ? '#1e1e1e' : '#ffffff');
    document.documentElement.style.setProperty('--backgroundGradient', 
      darkMode ? 'linear-gradient(135deg, #121212, #1e1e1e)' : 'linear-gradient(135deg, #f5f5f5, #ffffff)');
    document.documentElement.style.setProperty('--text', darkMode ? '#ffffff' : '#333333');
    document.documentElement.style.setProperty('--focusGlow', `rgba(${r}, ${g}, ${b}, 0.4)`);
    document.documentElement.style.setProperty('--homeGradient', homeGradient);

    // Сохраняем в localStorage
    try {
      localStorage.setItem('customThemeColor', color);
      localStorage.setItem('isDarkMode', String(darkMode));
    } catch (error) {
      console.error('Error saving theme:', error);
    }

    setCustomColor(color);
  };

  const toggleDarkMode = () => {
    const newDarkMode = !isDarkMode;
    setIsDarkMode(newDarkMode);
    generateThemeFromColor(selectedColor, newDarkMode);
  };

  useEffect(() => {
    if (selectedColor) {
      generateThemeFromColor(selectedColor, isDarkMode);
    }
  }, [isDarkMode]);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h3 className={styles.title}>{t('chooseYourColor')}</h3>
        <div className={styles.controls}>
          <button 
            className={styles.toggleButton}
            onClick={toggleDarkMode}
            title={isDarkMode ? t('switchToLightMode') : t('switchToDarkMode')}
          >
            <FontAwesomeIcon icon={isDarkMode ? faMoon : faSun} />
            <span>{isDarkMode ? t('dark') : t('light')}</span>
          </button>
          <button 
            className={styles.toggleButton}
            onClick={() => setShowPicker(!showPicker)}
          >
            <FontAwesomeIcon icon={showPicker ? faEyeSlash : faEye} />
            <span>{showPicker ? t('hidePalette') : t('showPalette')}</span>
          </button>
        </div>
      </div>

      <div className={styles.presetColors}>
        {presetColors.map((color) => (
          <button
            key={color}
            className={`${styles.presetButton} ${selectedColor === color ? styles.active : ''}`}
            style={{ backgroundColor: color }}
            onClick={() => handlePresetClick(color)}
            title={color}
          >
            {selectedColor === color && <span className={styles.checkmark}>✓</span>}
          </button>
        ))}
      </div>

      {showPicker && (
        <div className={styles.pickerContainer}>
          <canvas
            ref={canvasRef}
            width={300}
            height={200}
            className={styles.canvas}
            onClick={handleCanvasClick}
          />
          <div className={styles.selectedColor}>
            <div 
              className={styles.colorPreview}
              style={{ backgroundColor: selectedColor }}
            />
            <span className={styles.colorCode}>{selectedColor}</span>
          </div>
        </div>
      )}
    </div>
  );
}